import { Button, Divider, Flex, FormControl, FormLabel, HStack, Heading, IconButton, Input, Select, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useLocation, useNavigate } from 'react-router-dom';

import { FiArrowLeft } from 'react-icons/fi';
import { parcelDeliveryFromValidation } from '../../utils';
import { parcelDeliveryType } from '../../types';
import { updateParcelDelivery } from '../../store/actions';
import { useFormik } from 'formik';

export const EditParcelDelivery = () => {

    const { pickup_points } = useAppSelector(state => state.pickupPointsReducer);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const { state } = location;
    const { parcel }: { parcel: parcelDeliveryType } = state;

    const parcelDeliveryForm = useFormik({
        initialValues: {
            company_id: '',
            parcel_id: '',
            first_name: '',
            last_name: '',
            id_number: '',
            email: '',
            phone: '',
            amount: 0,
            status: 'received',
            pickup_address: '',
            dropoff_address: '',
            payment_status: 'unpaid',
            is_customer_location: false,
            address: ''
        },
        validateOnMount: true,
        validationSchema: parcelDeliveryFromValidation,
        onSubmit: (values: parcelDeliveryType) => {
            parcelDeliveryForm.setSubmitting(true);
            dispatch(updateParcelDelivery(values))
                .then(() => navigate('/parcels'))
                .finally(() => parcelDeliveryForm.setSubmitting(false));
        }
    });

    const updateFormFields = useCallback(() => {
        parcelDeliveryForm.setFieldValue('company_id', parcel?.company_id);
        parcelDeliveryForm.setFieldValue('parcel_id', parcel?.parcel_id);
        parcelDeliveryForm.setFieldValue('first_name', parcel?.first_name);
        parcelDeliveryForm.setFieldValue('last_name', parcel?.last_name);
        parcelDeliveryForm.setFieldValue('id_number', parcel?.id_number);
        parcelDeliveryForm.setFieldValue('email', parcel?.email);
        parcelDeliveryForm.setFieldValue('phone', parcel?.phone);
        parcelDeliveryForm.setFieldValue('amount', parcel?.amount);
        parcelDeliveryForm.setFieldValue('amount_status', parcel?.payment_status);
        parcelDeliveryForm.setFieldValue('status', parcel?.status);
        parcelDeliveryForm.setFieldValue('pickup_address', parcel?.pickup_address);
        parcelDeliveryForm.setFieldValue('dropoff_address', parcel?.dropoff_address);
        parcelDeliveryForm.setFieldValue('address', parcel?.address);
        parcelDeliveryForm.setFieldValue('is_customer_location', parcel?.is_customer_location);
    }, []);

    useEffect(() => {
        updateFormFields();
    }, []);

    return (
        <>
            <Flex
                minH={'-webkit-fill-available'}
                align={'center'}
                justify={'center'}
                pt={2}
                pb={10}
            >
                <Stack bgColor={'white'} borderRadius={6} p={6}>
                    <HStack spacing={6}>
                        <IconButton
                            aria-label={'Go back'}
                            icon={<FiArrowLeft />}
                            onClick={() => navigate('/parcels')}
                        />
                        <Heading size={'md'}>Edit Parcel Delivery.</Heading>
                    </HStack>
                    <Divider py={2} />
                    <Stack pt={4}>
                        <form onSubmit={parcelDeliveryForm.handleSubmit}>
                            <SimpleGrid columns={[1, 1, 2, 2, 2]} spacing={4}>
                                <FormControl mb={3} isRequired isReadOnly>
                                    <FormLabel>First name</FormLabel>
                                    <Input
                                        name='first_name'
                                        type='text'
                                        value={parcelDeliveryForm.values.first_name}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                        isDisabled
                                    />
                                    {parcelDeliveryForm.errors.first_name && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.first_name}</Text>}
                                </FormControl>
                                <FormControl mb={3} isRequired isReadOnly>
                                    <FormLabel>Last name</FormLabel>
                                    <Input
                                        name='last_name'
                                        type='text'
                                        value={parcelDeliveryForm.values.last_name}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                        isDisabled
                                    />
                                    {parcelDeliveryForm.errors.last_name && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.last_name}</Text>}
                                </FormControl>
                            </SimpleGrid>
                            <SimpleGrid columns={[1, 1, 2, 2, 2]} spacing={4}>
                                <FormControl mb={3}>
                                    <FormLabel>Id number</FormLabel>
                                    <Input
                                        name='id_number'
                                        type='text'
                                        value={parcelDeliveryForm.values.id_number}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                        isDisabled
                                    />
                                    {parcelDeliveryForm.errors.id_number && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.id_number}</Text>}
                                </FormControl>
                                <FormControl mb={3} isRequired isReadOnly>
                                    <FormLabel>Phone</FormLabel>
                                    <Input
                                        name='phone'
                                        type='text'
                                        value={parcelDeliveryForm.values.phone}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                    />
                                    {parcelDeliveryForm.errors.phone && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.phone}</Text>}
                                </FormControl>
                            </SimpleGrid>
                            <FormControl mb={3} isRequired isReadOnly>
                                <FormLabel>Email</FormLabel>
                                <Input
                                    name='email'
                                    type='email'
                                    value={parcelDeliveryForm.values.email}
                                    onChange={parcelDeliveryForm.handleChange}
                                    onBlur={parcelDeliveryForm.handleBlur}
                                    isDisabled
                                />
                                {parcelDeliveryForm.errors.email && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.email}</Text>}
                            </FormControl>
                            <SimpleGrid columns={[1, 1, 2, 2, 2]} spacing={4}>
                                <FormControl mb={3} isRequired>
                                    <FormLabel>Pickup address</FormLabel>
                                    <Select
                                        name='pickup_address'
                                        placeholder='Select pickup point'
                                        value={parcelDeliveryForm.values.pickup_address}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                    >
                                        {
                                            pickup_points?.map((pickup_point: any, index: number) => <option value={pickup_point?.name} key={index} style={{ textTransform: 'capitalize' }}>{pickup_point?.name}</option>)
                                        }
                                    </Select>
                                    {parcelDeliveryForm.errors.pickup_address && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.pickup_address}</Text>}
                                </FormControl>
                                <FormControl mb={3} isRequired>
                                    <FormLabel>Droppoff address</FormLabel>
                                    <Select
                                        name='dropoff_address'
                                        placeholder='Select droppoff point'
                                        value={parcelDeliveryForm.values.dropoff_address}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                        minW={'300px'}
                                    >
                                        {
                                            pickup_points?.map((pickup_point: any, index: number) => <option value={pickup_point?.name} key={index} style={{ textTransform: 'capitalize' }}>{pickup_point?.name}</option>)
                                        }
                                    </Select>
                                    {parcelDeliveryForm.errors.dropoff_address && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.dropoff_address}</Text>}
                                </FormControl>
                            </SimpleGrid>
                            <SimpleGrid columns={[1, 1, 2, 2, 2]} spacing={4}>
                                <FormControl mb={3} isRequired>
                                    <FormLabel>Amount</FormLabel>
                                    <Input
                                        name='amount'
                                        type='text'
                                        value={parcelDeliveryForm.values.amount}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                    />
                                    {parcelDeliveryForm.errors.amount && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.amount}</Text>}
                                </FormControl>
                                <FormControl mb={3} isRequired>
                                    <FormLabel>Payment status</FormLabel>
                                    <Select
                                        name='payment_status'
                                        placeholder='Select payment status'
                                        value={parcelDeliveryForm.values.payment_status}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                        minW={'300px'}
                                    >
                                        <option value='paid'>Paid</option>
                                        <option value='unpaid'>Unpaid</option>
                                    </Select>
                                    {parcelDeliveryForm.errors.payment_status && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.payment_status}</Text>}
                                </FormControl>
                            </SimpleGrid>
                            <SimpleGrid columns={[1, 1, 2, 2, 2]} spacing={4}>
                                <FormControl mb={3} isRequired>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        name='status'
                                        placeholder='Select status'
                                        value={parcelDeliveryForm.values.status}
                                        onChange={parcelDeliveryForm.handleChange}
                                        onBlur={parcelDeliveryForm.handleBlur}
                                        minW={'300px'}
                                    >
                                        <option value='received'>Received</option>
                                        <option value='dispatched'>Dispatched</option>
                                        <option value='arrived'>Arrived</option>
                                        <option value='delivered'>Delivered</option>
                                        <option value='returned'>Returned</option>
                                    </Select>
                                    {parcelDeliveryForm.errors.status && <Text fontSize='sm' color='red.400'>{parcelDeliveryForm.errors.status}</Text>}
                                </FormControl>
                            </SimpleGrid>
                            <Stack mt={2}>
                                <Button
                                    colorScheme={'yellow'}
                                    type={'submit'}
                                    isDisabled={parcelDeliveryForm.isSubmitting || !parcelDeliveryForm.isValid}
                                    isLoading={parcelDeliveryForm.isSubmitting}
                                    minW={200}
                                    alignSelf={'flex-end'}
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </form>
                    </Stack>
                </Stack>
            </Flex>
        </>
    );
};